/* eslint-disable max-len */
import React, { useContext, useEffect, useCallback, useState, useMemo } from 'react';

import PropTypes from 'prop-types';
import { ViewModelServiceContext, ViewModelForm, useDataRefresh } from '@xengage/gw-portals-viewmodel-react';
import { useValidation } from '@xengage/gw-portals-validation-react';
import { FormattedMessage, useTranslator } from '@jutro/locale';
import { MLDisclosureUtil, MLLocalDateUtil } from 'ml-portals-util-js';
import { MLDriverVehicleUtil, MLNotificationBlockUtil } from 'ml-portals-util-react';
import { LocalDateUtil } from '@xengage/gw-portals-util-js';
import { useModal } from '@jutro/components';
import { MLTagManagerService } from 'ml-portals-services-react'
import { FormModalPopover, platformMessages as commonMessages } from 'ml-components-platform-react';
import _ from 'lodash';

import agentMetadata from './MLAgentDisclosureComponent.default.metadata.json5';
import styles from './MLDisclosureComponent.module.scss';
import messages from './MLDisclosureComponent.messages';

import addDisclosurePopupMetadata from './AddDisclosurePopup.metadata.json5';
import editDisclosurePopupMetadata from './EditDisclosurePopup.metadata.json5';
import removeDisclosurePopupMetadata from './RemoveDisclosurePopup.metadata.json5';
import actionPopupModalBodyStyles from './ActionPopup.module.scss';

import { IntlContext } from '@jutro/legacy/locale';

// MLDisclosure.js - ml-disclosure.html, one disclosure type, one disclosure table
// MLDiscloureTable.js

// For Page...
// HO - agent/qnb/MLHODisclosureCtrl -> qnb/MLHODsclosureCtrl
// PA, PUP - agent/qnb/MLDisclosureCtrl -> qnb/MLDisclosureCtrl

function MLDisclosureComponent(props) {
    const modalApi = useModal();
    const translator = useTranslator();

    const {
        id,
        onValidate,
        showValidationErrors,
        //
        disclosures,
        owners,
        type,
        mvrStatus,
        showAddButton,
        hasEditPermission,
        isPremiumChanged,
        isAgent,
        shouldDiscloseExcluded,
        policyHolder,
        drivers,
        isPersonalAuto,
        primaryNamedInsuredState,
        lineOfBusiness,
        updateProductModel,
        isModernProduct,
        isPolicyChange
    } = props;

    const {
        onValidate: setComponentValidation,
        isComponentValid
    } = useValidation(id);

    const generateClienId = () => {
        return `${Date.now()}-${Math.random().toString(36).substring(2, 9)}`
    }

    
    useEffect(() => {
        disclosures.forEach((d) => {
            if (!d?.clientId) {
                d.clientId = generateClienId();
            }
        });
    }, [disclosures]);


    const { refreshData } = useDataRefresh();

    useEffect(() => {
        if (onValidate) {
            onValidate(isComponentValid, id);
        }
    }, [id, onValidate, isComponentValid]);

    useEffect(() => {
        setTimeout(() => {
            const element = document.getElementsByClassName("MLAgentDisclosureComponentContainer");
            if (element.length > 0) {
                element[0].focus();
            }
        }, 100)
    }, []);

    const viewModelService = useContext(ViewModelServiceContext);
    const sourceTypelist = viewModelService.productMetadata.get('pc').types.getTypelist('IncidentSource_ML');
    const currencyTypelist = viewModelService.productMetadata.get('pc').types.getTypelist('Currency');
    const DEFAULT_PAID_AMOUNT = 0;
    const DEFAULT_CURRENCY = currencyTypelist.getCode('usd').code;
    const LOB_TYPE_HOP = 'HOP';
    const disclosureTypes = viewModelService.productMetadata.get('pc').types.getTypelist('IncidentDescription_ML');
    const disclosureTypelists = {
        [MLDisclosureUtil.getAccidentType()]: disclosureTypes,
        [MLDisclosureUtil.getClaimType()]: disclosureTypes,
        [MLDisclosureUtil.getViolationType()]: disclosureTypes,
        [MLDisclosureUtil.getLossType()]: viewModelService.productMetadata.get('pc').types.getTypelist('LossType_ML')
    };

    // 
    const isAccident = MLDisclosureUtil.isAccidentType(type);
    const isClaim = MLDisclosureUtil.isClaimType(type);
    const isViolation = MLDisclosureUtil.isViolationType(type);
    const isLoss = MLDisclosureUtil.isLossType(type);
    const showMvrMessage = !_.isUndefined(mvrStatus);
    const [hasManuallyEnteredDisclosures, setHasManuallyEnteredDisclosures] = useState(false);
    const showAdd = _.isUndefined(showAddButton) || showAddButton;
    const ownerWithAddress = owners.filter((owner) => {
        return owner.primaryAddress && owner.primaryAddress.state;
    });
    const hasDisclosureReviewToBuyStep = window?.location?.pathname.includes('disclosure-review-to-buy');

    const {
        getFullName
    } = MLDriverVehicleUtil()
    const {
        addErrorAlert,
        clearErrorAlerts
    } = MLNotificationBlockUtil();

    const intl = useContext(IntlContext);
    const getFormattedDateString = useCallback((gwDateVal) => {
        const date = LocalDateUtil.toMidnightDate(gwDateVal);
        return intl.formatDate(date, { year: 'numeric', month: '2-digit', day: '2-digit' });
    }, [intl]);


    // table
    const hasActiveDisclosures = () => {
        return disclosures && !!disclosures.find((d) => !d.removeReason);
    };

    const shouldShowMVRMessage = () => showMvrMessage && (!hasManuallyEnteredDisclosures || !hasActiveDisclosures());
    const shouldShowTable = () => !showMvrMessage || mvrStatus === MLDisclosureUtil.mvrStatus.FOUND_VIOLATIONS || hasActiveDisclosures();
    const shouldShowManualViolationMessage = () => showMvrMessage && isViolation && !hasActiveDisclosures() && showAdd;
    const shouldShowMVRWillRunMethod = () => mvrStatus === MLDisclosureUtil.mvrStatus.NOT_RUN_YET;
    const shouldShowMVRViolationsFoundMethod = () => mvrStatus === MLDisclosureUtil.mvrStatus.FOUND_VIOLATIONS && isPremiumChanged;
    const shouldShowMVRNoViolationsFoundRunMethod = () => mvrStatus === MLDisclosureUtil.mvrStatus.NO_VIOLATIONS;

    const getTableHeader = () => {
        if (isAccident) return translator(messages.accidentsHeader);
        if (isClaim) return translator(messages.claimsHeader);
        if (isViolation) return translator(messages.violationsHeader);
        if (isLoss) return translator(messages.lossesHeader);
        return '';
    };

    const getAddButtonText = () => {
        if (isAccident) return translator(messages.addAccidentsButtonText);
        if (isClaim) return translator(messages.addClaimsButtonText);
        if (isViolation) return translator(messages.addViolationsButtonText);
        if (isLoss) return translator(messages.addHomeLossesButtonText);
        return '';
    };

    const getModelDTO = () => {
        if (isAccident) return 'metlife.edge.capabilities.policycommon.disclosures.dto.AccidentDTOML';
        if (isClaim) return 'metlife.edge.capabilities.policycommon.disclosures.dto.ClaimDTOML';
        if (isViolation) return 'metlife.edge.capabilities.policycommon.disclosures.dto.ViolationDTOML';
        if (isLoss) return 'metlife.edge.capabilities.policycommon.disclosures.dto.LossDTOML';
        return '';
    };

    const getAddDisclosurePopupTitle = () => {
        if (isAccident) return translator(messages.addAccidentsPopupTitle);
        if (isClaim) return translator(messages.addClaimsPopupTitle);
        if (isViolation) return translator(messages.addViolationsPopupTitle);
        if (isLoss) return translator(messages.addHomeLossesPopupTitle);
        return '';
    };

    const getEditDisclosurePopupTitle = () => {
        if (isAccident) return translator(messages.editAccidentsPopupTitle);
        if (isClaim) return translator(messages.editClaimsPopupTitle);
        if (isViolation) return translator(messages.editViolationsPopupTitle);
        if (isLoss) return translator(messages.editHomeLossesPopupTitle);
        return '';
    };

    const getRemoveDisclosurePopupTitle = () => {
        if (isAccident) return translator(messages.removeAccidentsPopupTitle);
        if (isClaim) return translator(messages.removeClaimsPopupTitle);
        if (isViolation) return translator(messages.removeViolationsPopupTitle);
        if (isLoss) return translator(messages.removeHomeLossesPopupTitle);
        return '';
    };

    const getActionPopupFieldDateLabel = () => {
        if (isAccident) return translator(messages.accidentsPopupDateField);
        if (isClaim) return translator(messages.claimsPopupDateField);
        if (isViolation) return translator(messages.violationsPopupDateField);
        if (isLoss) return translator(messages.homeLossesPopupDateField);
        return '';
    };

    const getActionPopupFieldOwnerLabel = () => {
        if (isAccident) return translator(messages.accidentsPopupOwnerField);
        if (isClaim) return translator(messages.claimsPopupOwnerField);
        if (isViolation) return translator(messages.violationsPopupOwnerField);
        if (isLoss) return translator(messages.homeLossesPopupOwnerField);
        return '';
    };

    const getActionPopupFieldEditReasonLabel = () => {
        if (isAccident) return translator(messages.accidentsPopupEditReasonField);
        if (isClaim) return translator(messages.claimsPopupEditReasonField);
        if (isViolation) return translator(messages.violationsPopupEditReasonField);
        if (isLoss) return translator(messages.homeLossesPopupEditReasonField);
        return '';
    };

    const getActionPopupFieldRemoveReasonLabel = () => {
        if (isAccident) return translator(messages.accidentsPopupRemoveReasonField);
        if (isClaim) return translator(messages.claimsPopupRemoveReasonField);
        if (isViolation) return translator(messages.violationsPopupRemoveReasonField);
        if (isLoss) return translator(messages.homeLossesPopupRemoveReasonField);
        return '';
    };

    const setEditDisclosuresPermission = (source) => {
        if (source === 'customer_disclosed') {
            return true;
        }
        return false;
    };

    const isCustomerDisclosedEditable = (disclosure) => {
        return (_.isUndefined(hasEditPermission) || hasEditPermission)
            && sourceTypelist.getCode(disclosure.source).code === sourceTypelist.getCode('customer_disclosed').code;
    };

    const isEditable = (disclosure) => {
        return isCustomerDisclosedEditable(disclosure) || disclosure.classificationEditable;
    };

    const isRemovable = (disclosure) => {
        return isCustomerDisclosedEditable(disclosure);
    };

    const getSourceLabel = (source) => {
        if (source === 'customer_disclosed') {
            return translator(messages.customer);
        }
        if (source === 'lexisNexis' || source === 'mvr') {
            return translator(messages.report);
        }
        return null;
    };

    const getOwnerState = () => {
        // if (defaultPerson && defaultPerson.primaryAddress) {
        //     return defaultPerson.primaryAddress.state;
        // } 
        if (ownerWithAddress.length > 0 && ownerWithAddress[0].primaryAddress) {
            return ownerWithAddress[0].primaryAddress.state;
        }
        return primaryNamedInsuredState;
    };

    const getDriverType = (disclosure) => {
        if (disclosure.person && drivers) {
            disclosure.driverType = drivers.filter((d) => {
                return isPersonalAuto ? d.person.publicID === disclosure.person.publicID : d.contact.publicID === disclosure.person.publicID;
            })[0].type_ML;
        }
        return disclosure.driverType;
    };

    const verifyPaidAmount = (paidAmount) => {
        if (!isViolation) {
            if (!paidAmount.amount) {
                paidAmount.amount = DEFAULT_PAID_AMOUNT;
            }
            if (!paidAmount.currency) {
                paidAmount.currency = DEFAULT_CURRENCY;
            }
        }
    };

    const setPaidAmountIfNotViolation = (disclosure, newPaidAmount) => {
        const setDefaultPaidAmount = () => {
            disclosure.paidAmount = { amount: DEFAULT_PAID_AMOUNT, currency: DEFAULT_CURRENCY };
        };
        if (isViolation) {
            return;
        }
        if (!newPaidAmount) {
            setDefaultPaidAmount();
        } else {
            verifyPaidAmount(newPaidAmount);
            if (disclosure.paidAmount) {
                disclosure.paidAmount.amount = newPaidAmount.amount;
                disclosure.paidAmount.currency = newPaidAmount.currency;
            } else {
                disclosure.paidAmount = {
                    amount: newPaidAmount.amount,
                    currency: newPaidAmount.currency
                };
            }
        }
    };

    const getPersonFromOwners = (disclosure) => {
        let person = disclosure.person;
        if (disclosure.person) {
            person = owners.find((o) => o.publicID === disclosure.person.publicID);
        }
        return person;
    };

    const getVisibleDisclosures = () => {
        return _.orderBy(disclosures.filter((d) => d.type && _.isEmpty(d.removeReason)), ['date.year', 'date.month', 'date.day'], ['desc', 'desc', 'desc']);
    };

    const getNewDisclosure = () => {
        const newDisclosure = {
            source: sourceTypelist.getCode('customer_disclosed').code,
            type: null,
            editReason: null,
            removeReason: null,
            nameInsured: policyHolder || null,
            person: isLoss ? policyHolder : null,
            excludedDriver: null,
            state: getOwnerState()
        };
        setPaidAmountIfNotViolation(newDisclosure);
        return newDisclosure;
    };
    const showDuplicateDisclosureAlert = useCallback(() => {
        addErrorAlert(messages.duplicateDisclosureAlert, 'duplicateDisclosureAlert');
    }, [addErrorAlert]);

    const isWeatherLossEditable = (key, isFixedIdPresent) => {
        let index
        if(isFixedIdPresent) {
            index = getDisclosureIndex(key);
        } else {
             index = getDisclosureIndexByClientID(key);
        }
        if (index === -1) return;
        const resDisclosure = _.cloneDeep(disclosures[index]);
        const resDisclosureVM = viewModelService.create(resDisclosure, 'pc', getModelDTO());
        return !!resDisclosureVM?.weatherLossEditable?.value
    };

    const setWeatherLossValue = (key, value, isFixedIdPresent) => {
        let index
        if(isFixedIdPresent) {
            index = getDisclosureIndex(key)
        } else {
            index = getDisclosureIndexByClientID(key)
        }
        if (index === -1) return;
        const updateDisclosure = _.cloneDeep(disclosures[index]);
        const updateDisclosureVM = viewModelService.create(updateDisclosure, 'pc', getModelDTO());
        _.set(updateDisclosureVM.value, 'weatherLoss', value === 'true');
        disclosures[index] = updateDisclosureVM.value;
        refreshData();
    }

    const onAdd = () => {
        MLTagManagerService.sendLinkClickEvent(`${MLTagManagerService.getStepName()}_Add ${type}`);
        // Create a new disclosure and add it to the driver if saved
        const newDisclosureVM = viewModelService.create(getNewDisclosure(), 'pc', getModelDTO());

        const getMaxClaimDate = () => {
            const date = MLLocalDateUtil.date2LocalDate(new Date());
            date.day -= 1;
            return date;
        }

        const onResolve = (disclosureVM) => {
            const disclosure = disclosureVM.value;
            const disclosureCheck = _.filter(disclosures, (item) => item.type === disclosure?.type && item?.paidAmount?.amount === disclosure?.paidAmount?.amount && item?.person?.publicID === disclosure?.person
                && item?.date?.day === disclosure?.date?.day && item?.date?.month === disclosure?.date?.month && item?.date?.year === disclosure?.date?.year)
            if (disclosureCheck?.length < 1) {
                clearErrorAlerts();
                verifyPaidAmount(disclosure.paidAmount);
                // dropdownselect code == owner.publicID
                disclosure.person = owners.find((o) => o.publicID === disclosureVM.value.person);              
                disclosure.clientId = generateClienId();
                disclosures.push(disclosure);
                setHasManuallyEnteredDisclosures(true);
                MLTagManagerService.sendFormStepEvent(MLTagManagerService.getFormName(), `${MLTagManagerService.getStepName()}_Add new ${type}`);
                refreshData();
                if (updateProductModel) {
                    updateProductModel();
                }
                return true;
            } else {
                clearErrorAlerts();
                showDuplicateDisclosureAlert();
            }
        };

        const componentProps = {
            title: getAddDisclosurePopupTitle(),
            uiProps: addDisclosurePopupMetadata.pageContent,
            viewModel: newDisclosureVM,
            actionBtnLabel: commonMessages.mlSave,
            cancelBtnLabel: commonMessages.mlCancel,
            modalBodyStyles: actionPopupModalBodyStyles,
            getOverrideProps: () => {
                return {
                    actionDate: {
                        label: getActionPopupFieldDateLabel()
                    },
                    actionOwnerRow: {
                        visible: !isLoss
                    },
                    actionOwner: {
                        label: getActionPopupFieldOwnerLabel(),
                        availableValues: owners.map((owner) => {
                            return {
                                id: owner.publicID,
                                displayName: owner.displayFullName_Ext
                            };
                        })
                    },
                    actionAmountPaidRow: {
                        visible: !isViolation
                    }
                };
            }
        };

        modalApi.showModal(
            <FormModalPopover {...componentProps} />
        ).then((disclosureVM) => {
            onResolve(disclosureVM);
        });
    };

    const onEdit = (evt) => {
        // need clone for Edit, otherwise value changes even after cancelling popup
        const idx = disclosures.indexOf(getVisibleDisclosures()[evt.index]);
        const editDisclosure = _.clone(disclosures[idx]);
        const editDisclosureVM = viewModelService.create(editDisclosure, 'pc', getModelDTO());
        const customerDisclosedEditable = isCustomerDisclosedEditable(editDisclosure);

        const onResolve = (disclosureVM) => {
            const disclosure = disclosureVM.value;
            const disclosureCheck = _.filter(disclosures, (item) => item.type === disclosure?.type && item?.paidAmount?.amount === disclosure?.paidAmount?.amount && item?.person?.publicID === disclosure?.person?.publicID
                && item?.date?.day === disclosure?.date?.day && item?.date?.month === disclosure?.date?.month && item?.date?.year === disclosure?.date?.year)
            if (disclosureCheck?.length < 1) {
                clearErrorAlerts();
                verifyPaidAmount(disclosure.paidAmount);
                MLTagManagerService.sendFormStepEvent(MLTagManagerService.getFormName(), `${MLTagManagerService.getStepName()}_Add new ${type}`);
                disclosures[idx] = disclosure;
                refreshData();
                if (updateProductModel) {
                    updateProductModel();
                }
                return true;
            } else {
                clearErrorAlerts();
                showDuplicateDisclosureAlert();
            }
        };

        const hasNoEditReason = (disclosureVM) => {
            return _.isEmpty(disclosureVM.value.editReason);
        };

        const onFieldValueChange = (value, path) => {
            if (path === 'person.publicID') {
                editDisclosureVM.person = owners.find((o) => o.publicID === value);
            }
        };

        const componentProps = {
            title: getEditDisclosurePopupTitle(),
            uiProps: editDisclosurePopupMetadata.pageContent,
            viewModel: editDisclosureVM,
            actionBtnLabel: commonMessages.mlSave,
            disableButton: hasNoEditReason,
            cancelBtnLabel: commonMessages.mlCancel,
            modalBodyStyles: actionPopupModalBodyStyles,
            onFieldValueChange: onFieldValueChange,
            getOverrideProps: () => {
                return {
                    actionDateRow: {
                        visible: customerDisclosedEditable
                    },
                    actionDate: {
                        label: getActionPopupFieldDateLabel()
                    },
                    actionOwnerRow: {
                        visible: !isLoss && customerDisclosedEditable
                    },
                    actionOwner: {
                        label: getActionPopupFieldOwnerLabel(),
                        availableValues: owners.map((owner) => {
                            return {
                                id: owner.publicID,
                                displayName: owner.displayFullName_Ext
                            };
                        })
                    },
                    actionClassificationRow: {
                        visible: !!editDisclosure.classificationEditable
                    },
                    actionDescriptionRow: {
                        visible: customerDisclosedEditable
                    },
                    actionAmountPaidRow: {
                        visible: !isViolation && customerDisclosedEditable
                    },
                    actionReason: {
                        label: getActionPopupFieldEditReasonLabel()
                    }
                };
            }
        };

        modalApi.showModal(
            <FormModalPopover {...componentProps} />
        ).then(onResolve);
    };

    const onRemove = (evt) => {
        const idx = disclosures.indexOf(getVisibleDisclosures()[evt.index]);
        const removeDisclosure = disclosures[idx];
        const removeDisclosureVM = viewModelService.create(removeDisclosure, 'pc', getModelDTO());

        const onSaveAction = (disclosureVM) => {
            const disclosure = disclosureVM.value;
            MLTagManagerService.sendFormStepEvent(MLTagManagerService.getFormName(), `${MLTagManagerService.getStepName()}_Add new ${type}`);
            disclosures[idx] = disclosure;
            refreshData();
            if (updateProductModel && isPersonalAuto) {
                updateProductModel();
            }
            return true;
        };

        const hasNoRemoveReason = (disclosureVM) => {
            return _.isEmpty(disclosureVM.value.removeReason);
        };

        const componentProps = {
            title: getRemoveDisclosurePopupTitle(),
            uiProps: removeDisclosurePopupMetadata.pageContent,
            viewModel: removeDisclosureVM,
            actionBtnLabel: commonMessages.mlSave,
            disableButton: hasNoRemoveReason,
            cancelBtnLabel: commonMessages.mlCancel,
            modalBodyStyles: actionPopupModalBodyStyles,
            getOverrideProps: () => {
                return {
                    actionReason: {
                        label: getActionPopupFieldRemoveReasonLabel()
                    }
                };
            },
            onSaveAction
        };

        modalApi.showModal(
            <FormModalPopover {...componentProps} />
        );
    };

    const resolvers = {
        resolveClassNameMap: styles,
        resolveCallbackMap: {
            onAdd,
            onEdit,
            onRemove
        }
    };

    const getTypeKeyDescription = (typeKeyName) => {
        return translator({
            id: typeKeyName,
            defaultMessage: typeKeyName
        });
    };

    const getDisclosureIndex = (key) => disclosures.findIndex(d => d?.fixedID === key)
    const getDisclosureIndexByClientID = (key) => disclosures.findIndex(d => d?.clientId === key)

    const generateDisclsureOverrides = () => {

        const overrides = getVisibleDisclosures().map((entry, index) => {
            const key = entry.fixedID;
            return {
                [`source${index}`]: {
                    content: getSourceLabel(entry.source)
                },
                [`date${index}`]: {
                    content: getFormattedDateString(entry.date)
                },
                [`cellDriverName${index}`]: {
                    visible: lineOfBusiness !== LOB_TYPE_HOP
                },
                [`driverName${index}`]: {
                    content: entry.person ? getFullName(entry.person).toUpperCase() : entry.policyHolder
                },
                [`cellExcludedDriver${index}`]: {
                    visible: shouldDiscloseExcluded
                },
                [`excludedDriver${index}`]: {
                    content: getDriverType(entry) === 'excluded' ? translator(messages.yes) : translator(messages.no)
                },
                [`description${index}`]: {
                    content: entry.type?.name || getTypeKeyDescription(disclosureTypelists[type].getCode(entry.type)?.name)
                },
                [`cellWeatherLoss${index}`]: {
                    visible: !isPolicyChange && isClaim && isModernProduct && isPersonalAuto && getOwnerState() === 'AR'
                },
                [`weatherLoss${index}`]: {
                },
                [`weatherLossDropDown${index}`]: {
                    availableValues: [
                        {
                            code: 'true',
                            name: translator(messages.weatherLossYes)
                        },
                        {
                            code: 'false',
                            name: translator(messages.weatherLossNo)
                        }
                    ],
                    visible: isWeatherLossEditable(key ?? entry.clientId, key),
                    path: 'weatherLoss.value',
                    value: typeof entry?.weatherLoss === 'boolean'
                            ? entry.weatherLoss.toString()
                            : entry?.weatherLoss?.value ?? '',
                    onValueChange: (value) => {
                        setWeatherLossValue(key ?? entry.clientId,value, key);
                    }
                },
                [`cellAmountPaid${index}`]: {
                    visible: !isViolation,
                },
                [`amountPaid${index}`]: {
                    content: entry.paidAmount ? `$${MLDisclosureUtil.setAmountFormattedWithCommas(entry.paidAmount.amount)}` : ''
                },
                [`mobileActionLabel${index}`]: {
                    visible: isAgent ? setEditDisclosuresPermission(entry.source) : (isEditable(entry) || isRemovable(entry))
                },
                [`actionEdit${index}`]: {
                    visible: isAgent ? setEditDisclosuresPermission(entry.source) : isEditable(entry)
                },
                [`actionRemove${index}`]: {
                    visible: isAgent ? setEditDisclosuresPermission(entry.source) : isRemovable(entry)
                }
            };
        });

        return Object.assign({}, ...overrides);
    };

    const overrides = {
        tableHeader: {
            content: getTableHeader()
        },
        disclosureTable: {
            data: getVisibleDisclosures()
        },
        driverHeader: {
            visible: !isLoss
        },
        excludedDriverHeader: {
            visible: shouldDiscloseExcluded
        },
        nameHeader: {
            visible: isLoss && lineOfBusiness !== LOB_TYPE_HOP
        },
        amountPaidHeader: {
            visible: !isViolation
        },
        weatherLossHeaderContainer: {
            visible:!isPolicyChange && isClaim && isModernProduct && isPersonalAuto && getOwnerState() === 'AR' 
        },
        mvrMessageContainer: {
            visible: shouldShowMVRMessage()
        },
        mvrWillRunMethod: {
            visible: shouldShowMVRWillRunMethod()
        },
        mvrViolationsFoundMethond: {
            visible: shouldShowMVRViolationsFoundMethod()
        },
        mvrNoViolationsFoundRunMethod: {
            visible: shouldShowMVRNoViolationsFoundRunMethod(),
            content: hasDisclosureReviewToBuyStep ? (
                <FormattedMessage
                    {...messages.showMVRNoViolationsFoundRunMethodPrebind}
                    values={{
                        MVRHasBeenRun: (
                            <strong>{translator(messages.mvrHasBeenRun)}</strong>
                        ),
                    }}
                />
            ) : (
                <FormattedMessage
                    {...messages.showMVRNoViolationsFoundRunMethod}

                />
            )
        },
        manualViolationMessageContainer: {
            visible: shouldShowManualViolationMessage()
        },
        disclosureContainer: {
            visible: shouldShowTable()
        },
        disclosureTableContainer: {
            visible: !_.isEmpty(disclosures),
            'aria-description': getTableHeader()
        },
        noDislosure: {
            visible: _.isEmpty(disclosures),
            content: <FormattedMessage
                {...messages.noXAvailable}
                values={{
                    header: getTableHeader().toLowerCase()
                }}
            />
        },
        addButton: {
            visible: showAdd,
            content: getAddButtonText()
        },
        ...generateDisclsureOverrides()
    };

    const nonAgentEntryList = useMemo(() => {
        return disclosures.map((disclosure) => {
            let entry;
            if (!disclosure.person) {
                entry = {
                    date: disclosure.date,
                    type: disclosure.type,
                    ownerFullName: policyHolder
                }
            } else {
                if (drivers) {
                    disclosure.driverType = drivers.filter((d) => {
                        return isPersonalAuto ? d.person.publicID === disclosure.person.publicID : d.contact.publicID === disclosure.person.publicID;
                    })[0].type_ML;
                }
                const fullName = getFullName(disclosure.person);
                entry = {
                    date: disclosure.date,
                    type: disclosure.type,
                    ownerFullName: fullName || disclosure.nameInsured

                }
            }
            return entry;
        });
    }, [disclosures, drivers, getFullName, isPersonalAuto, policyHolder])

    function disclosureList() {
        const listItems = nonAgentEntryList.map((entry) =>
            <li>
                <span className='font-weight-bold'>
                    {entry.ownerFullName}
                </span>
                {` ${getFormattedDateString(entry.date)} ${getTypeKeyDescription(disclosureTypelists[type].getCode(entry.type)?.name)}`}
            </li>
        );
        return (
            <ul className='styled-ul-list check-icon'>
                {listItems}
            </ul>
        );
    }

    return (
        isAgent ?
            <ViewModelForm
                uiProps={agentMetadata.componentContent}
                overrideProps={overrides}
                onValidationChange={setComponentValidation}
                showErrors={showValidationErrors}
                componentMap={resolvers.resolveComponentMap}
                callbackMap={resolvers.resolveCallbackMap}
                classNameMap={resolvers.resolveClassNameMap}
            />
            :
            disclosureList()
    );
}

MLDisclosureComponent.propTypes = {
    id: PropTypes.string,
    onValidate: PropTypes.func,
    showValidationErrors: PropTypes.bool,
    isPermiumChanged: PropTypes.bool,
    hasEditPermission: PropTypes.bool,
    shouldDiscloseExcluded: PropTypes.bool,
    isPersonalAuto: PropTypes.bool,
    updateProductModel: PropTypes.func
};

MLDisclosureComponent.defaultProps = {
    id: undefined,
    onValidate: undefined,
    showValidationErrors: undefined,
    isPermiumChanged: false,
    hasEditPermission: undefined,
    shouldDiscloseExcluded: false,
    isPersonalAuto: false,
    updateProductModel: undefined
};

export default MLDisclosureComponent;
